import { useEffect, useRef, type RefObject } from 'react';

/**
 * The iframe is as tall as the host was told: tell it again whenever the
 * content changes height (cards finishing their loads, a custom period's row
 * appearing, a report opening). The observer fires on mount and on every
 * change; the host gets at most one message per frame, however many fire.
 * `fit` is `null` until there is a frame to tell.
 */
export function useFitFrameOnResize(
  rootRef: RefObject<HTMLElement | null>,
  fit: (() => void) | null
): void {
  const pendingFit = useRef<number | null>(null);
  useEffect(() => {
    const node = rootRef.current;
    if (!fit || !node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (pendingFit.current !== null) return;
      pendingFit.current = requestAnimationFrame(() => {
        pendingFit.current = null;
        fit();
      });
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (pendingFit.current !== null) cancelAnimationFrame(pendingFit.current);
      pendingFit.current = null;
    };
  }, [rootRef, fit]);
}
